import * as React from 'react';
import { useIntl } from 'react-intl';

import { DrawerContext, CloseLineIcon } from '@notino/react-styleguide';
import { IStock } from '@notino/shared/definitions/custom-definitions';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';
import messages from '@containers/ProductDetailContainer/messages';
import { useAppendDisplayTypeForAbTest } from '@containers/ProductDetailContainer/utils';
import { dataLabelsProp } from '@helpers/utils';

import { VariantLink } from '../../variantsStyled';
import {
  IVariantItemProps,
  getContrastingIconColor,
} from '../VariantsColorPickers/ColorPicker';

import { OffsetScroll } from './components/OffsetScroll';
import { COLOR_CIRCLE_SIZE } from './constants';
import { ItemStatus } from './ItemStatus';
import {
  VariantListItem,
  Variant,
  BorderedColorCircle,
  AdditionalInfo,
  VariantListItemInfo,
} from './styled';
import { trackDrawerVariantClick } from './tracking';
import { getItemColors } from './utils';

export interface ColorCirclePickerProps extends IVariantItemProps {
  getTimeFromInit: () => number;
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
}

export const ColorCirclePicker: React.FC<ColorCirclePickerProps> = ({
  item,
  isSelected,
  getTimeFromInit,
  product,
}) => {
  const { closeDrawer } = DrawerContext.useDrawerContext();
  const variantUrl = useAppendDisplayTypeForAbTest(item.url);

  const { formatMessage } = useIntl();

  const isOutOfStock = item.stockAvailability.code === IStock.outOfStock;
  const withoutColors = !item.colors || item.colors.length === 0;
  const colors = getItemColors(item);

  const handleClick = () => {
    trackDrawerVariantClick({ timing: getTimeFromInit() });
    closeDrawer();
  };

  return (
    <VariantListItem
      aria-label={isOutOfStock ? formatMessage(messages.outOfStock) : undefined}
      aria-disabled={isOutOfStock}
    >
      {isSelected && <OffsetScroll />}
      <ErrorBoundary>
        <VariantLink
          replace={true}
          to={variantUrl}
          onClick={handleClick}
          id={`pd-variant-${item.webId}`}
          className={isSelected ? 'pd-variant-selected' : ''}
          {...dataLabelsProp(item.attributes)}
        >
          <Variant isSelected={isSelected}>
            <VariantListItemInfo isOutOfStock={isOutOfStock}>
              <BorderedColorCircle
                width={COLOR_CIRCLE_SIZE}
                height={COLOR_CIRCLE_SIZE}
                colors={colors}
                darkerBorder={withoutColors}
              >
                {isOutOfStock && (
                  <CloseLineIcon
                    color={getContrastingIconColor(colors)}
                    width="100%"
                    height="100%"
                  />
                )}
              </BorderedColorCircle>
              <AdditionalInfo>{item.additionalInfo}</AdditionalInfo>
            </VariantListItemInfo>

            <ItemStatus item={item} product={product} />
          </Variant>
        </VariantLink>
      </ErrorBoundary>
    </VariantListItem>
  );
};
